import DS from 'ember-data';
import { ModelRegistry } from 'ember-data/model';

type JSONAPIRelationship = {
  id: string;
  type: keyof ModelRegistry;
};

type JSONAPIDoc = {
  type: keyof ModelRegistry;
  id: string;
  relationships: Record<string | number | symbol, { data: JSONAPIRelationship[] | JSONAPIRelationship }>;
  attributes: Record<string | number | symbol, any>;
};

// This takes a model and serializes it into a json-api compatible format. This
// is mostly helpful when using `store.push`, which expects data to be in the
// json-api format and not our serialized back end format.
export function serializeToJSONAPI(
  store: DS.Store,
  modelName: keyof ModelRegistry,
  model: DS.Model,
): { data: JSONAPIDoc } {
  let data: JSONAPIDoc = {
    type: modelName,
    id: model.id,
    relationships: {},
    attributes: {},
  };

  // Loop through all of the attributes in the model definition and add the
  // current value from the model instance to the data
  store.modelFor(modelName).eachAttribute((name: string) => {
    data.attributes[name] = (model as any)[name];
  });

  // Loop through all of the defined relationships on the model and serialize
  // them into a json-api compatible format
  model.eachRelationship<any>((name, meta) => {
    let existingRelationships = data.relationships[name] ?? { data: [] };
    let relationship = (model[meta.kind] as any)(name);
    let relationData: JSONAPIRelationship | JSONAPIRelationship[] | null = null;

    if (meta.kind === 'hasMany') {
      relationData = [
        ...(existingRelationships.data as JSONAPIRelationship[]),
        ...relationship.identifiers
          // Some relationships are defined but empty
          .filter((identifier: any) => identifier.id != null)
          .map((identifier: any) => ({ id: identifier.id, type: meta.type })),
      ];
    } else if (meta.kind === 'belongsTo' && relationship.id() != null) {
      relationData = { id: relationship.id(), type: meta.type };
    }

    if (relationData == null) {
      return;
    }

    data.relationships[name] = {
      ...existingRelationships,
      data: relationData,
    };
  });

  return { data };
}
